import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { Auth } from 'aws-amplify';
import ec2StatusBroadcastService from '../services/ec2StatusBroadcastService';

const EC2LoadingContext = createContext();

const STORAGE_KEY = 'ec2LoadingStates';

export const useEC2Loading = () => {
  const context = useContext(EC2LoadingContext);
  if (!context) {
    throw new Error('useEC2Loading must be used within an EC2LoadingProvider');
  }
  return context;
};

export const EC2LoadingProvider = ({ children }) => {
  // Initialize state from localStorage
  const loadStatesFromStorage = () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Validate and filter out expired states (older than 10 minutes)
        const now = Date.now();
        const validStates = {};
        Object.keys(parsed).forEach(projectId => {
          const state = parsed[projectId];
          // Keep state if it's less than 10 minutes old
          if (state.startTime && (now - state.startTime) < 600000) {
            validStates[projectId] = state;
          }
        });
        return validStates;
      }
    } catch (error) {
      console.error('Error loading EC2 loading states from localStorage:', error);
    }
    return {};
  };

  // Store loading states for multiple projects
  // Initialize from localStorage on mount
  const [loadingStates, setLoadingStates] = useState(() => loadStatesFromStorage());
  const unsubscribeRef = useRef(null);

  // Save states to localStorage whenever they change
  useEffect(() => {
    try {
      if (Object.keys(loadingStates).length > 0) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(loadingStates));
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch (error) {
      console.error('Error saving EC2 loading states to localStorage:', error);
    }
  }, [loadingStates]);

  // Subscribe to EC2 status broadcasts to sync across all users/tabs
  useEffect(() => {
    unsubscribeRef.current = ec2StatusBroadcastService.subscribe((eventType, projectId, data) => {
      if (!projectId) return;

      const broadcastInitiatingUserEmail =
        data?.initiatingUserEmail ||
        data?.initiating_user_email ||
        null;

      console.log(`📡 Received EC2 status broadcast: ${eventType} for project ${projectId}`);

      if (eventType === 'ec2_started') {
        // Another user launched an instance - start loading state if not already loading
        setLoadingStates(prev => {
          if (prev[projectId]?.isLoading) {
            // Already loading, don't override
            return prev;
          }
          console.log(`📡 Starting loading state for project ${projectId} (broadcast from another user)`);
          return {
            ...prev,
            [projectId]: {
              isLoading: true,
              projectName: `Project ${projectId}`,
              startTime: Date.now(),
              state: 'checking',
              tempMessage: null,
              externalUrl: null,
              isFreshLaunch: false, // Not a fresh launch from this user
              progress: 0,
              statusMessage: 'Launching 3D Viewer...',
              currentStep: 'checking',
              initiatingUserEmail: broadcastInitiatingUserEmail, // May be provided by backend broadcast
              launchedByThisClient: false
            }
          };
        });
      } else if (eventType === 'ec2_progress') {
        // Real-time progress update - sync progress, status, and message for all users
        const progress = data.progress || 0;
        const status = data.status || '';
        const ready = data.ready || false;
        
        setLoadingStates(prev => {
          // If loading state doesn't exist, create it (in case we missed the start event)
          if (!prev[projectId]) {
            console.log(`📡 Creating loading state for project ${projectId} from progress update`);
            return {
              ...prev,
              [projectId]: {
                isLoading: true,
                projectName: `Project ${projectId}`,
                startTime: Date.now(),
                state: 'checking',
                tempMessage: null,
                externalUrl: null,
                isFreshLaunch: false,
                progress: progress,
                statusMessage: status || 'Launching 3D Viewer...',
                currentStep: ready ? 'success' : 'checking',
                initiatingUserEmail: broadcastInitiatingUserEmail, // May be provided by backend broadcast
                launchedByThisClient: false
              }
            };
          }
          
          // Update existing loading state with latest progress
          console.log(`📡 Updating progress for project ${projectId}: ${prev[projectId].progress}% → ${progress}% (${status})`);
          return {
            ...prev,
            [projectId]: {
              ...prev[projectId],
              progress: progress,
              statusMessage: status || prev[projectId].statusMessage || 'Launching 3D Viewer...',
              currentStep: ready ? 'success' : prev[projectId].currentStep || 'checking',
              initiatingUserEmail:
                prev[projectId].initiatingUserEmail || broadcastInitiatingUserEmail || null,
              launchedByThisClient: prev[projectId].launchedByThisClient === true
            }
          };
        });
      } else if (eventType === 'ec2_ready') {
        // Instance reached 100% - update progress and mark as ready (backward compatibility)
        setLoadingStates(prev => {
          if (!prev[projectId]) {
            return prev;
          }
          console.log(`📡 Updating project ${projectId} to ready state (broadcast)`);
          return {
            ...prev,
            [projectId]: {
              ...prev[projectId],
              progress: 100,
              statusMessage: '3D viewer is active',
              currentStep: 'success'
            }
          };
        });
      }
    });

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, []);

  // Helper function to get current user's email
  const getCurrentUserEmail = async () => {
    try {
      const user = await Auth.currentAuthenticatedUser();
      if (user && user.attributes && user.attributes.email) {
        return user.attributes.email;
      } else if (user && user.signInUserSession && user.signInUserSession.idToken) {
        const idToken = user.signInUserSession.idToken.jwtToken;
        const payload = JSON.parse(atob(idToken.split('.')[1]));
        return payload.email || payload.username;
      }
    } catch (error) {
      console.warn('⚠️ Could not get user email from token, using sessionStorage:', error);
      return sessionStorage.getItem('userEmail');
    }
    return null;
  };

  const startLoading = async (projectId, projectName, externalUrl) => {
    const sessionUserEmail = sessionStorage.getItem('userEmail');
    // Get the current user's email to track who initiated the launch
    // Use async/await but don't block - if email is not available immediately, 
    // we'll set it later or use null (which means this user didn't initiate)
    getCurrentUserEmail().then(initiatingUserEmail => {
      // Update the state with the email once we have it
      setLoadingStates(prev => {
        if (!prev[projectId]) return prev; // State was cleared
        return {
          ...prev,
          [projectId]: {
            ...prev[projectId],
            initiatingUserEmail: initiatingUserEmail
          }
        };
      });
    }).catch(error => {
      console.warn('⚠️ Could not get user email for initiatingUserEmail:', error);
    });
    
    // Set initial state immediately (email will be added asynchronously)
    setLoadingStates(prev => ({
      ...prev,
      [projectId]: {
        isLoading: true,
        projectName: projectName || `Project ${projectId}`,
        startTime: Date.now(),
        state: 'checking',
        tempMessage: null,
        externalUrl: externalUrl || null,
        isFreshLaunch: true, // Flag to indicate this is a new launch, not a restoration
        progress: 0, // Track progress percentage
        statusMessage: 'Launching 3D Viewer...', // Track current status message
        currentStep: 'checking', // Track current step
        initiatingUserEmail: sessionUserEmail || null, // Set immediately, then refine asynchronously above
        launchedByThisClient: true
      }
    }));
  };

  const stopLoading = (projectId) => {
    setLoadingStates(prev => {
      const newStates = { ...prev };
      delete newStates[projectId];
      return newStates;
    });
  };

  const isProjectLoading = (projectId) => {
    return loadingStates[projectId]?.isLoading || false;
  };

  const getLoadingProject = (projectId) => {
    return loadingStates[projectId];
  };

  const getAllLoadingProjects = () => {
    return Object.keys(loadingStates).map(projectId => ({
      projectId,
      ...loadingStates[projectId]
    }));
  };

  const getProjectLaunchState = (projectId) => {
    return loadingStates[projectId]?.state || null;
  };

  const updateLoadingState = useCallback((projectId, updates) => {
    setLoadingStates(prev => {
      if (!prev[projectId]) {
        return prev;
      }
      // Only update if values actually changed to prevent unnecessary re-renders
      const currentState = prev[projectId];
      const hasChanges = Object.keys(updates).some(key => {
        if (key === 'progress') {
          // Round both values for comparison to avoid floating point issues
          return Math.round(currentState[key] || 0) !== Math.round(updates[key] || 0);
        }
        return currentState[key] !== updates[key];
      });
      
      if (!hasChanges) {
        return prev; // No changes, return same reference
      }
      
      return {
        ...prev,
        [projectId]: {
          ...prev[projectId],
          ...updates
        }
      };
    });
  }, []);

  const value = {
    loadingStates,
    startLoading,
    stopLoading,
    isProjectLoading,
    getLoadingProject,
    getAllLoadingProjects,
    getProjectLaunchState,
    updateLoadingState
  };

  return (
    <EC2LoadingContext.Provider value={value}>
      {children}
    </EC2LoadingContext.Provider>
  );
};

